import { ScenarioTypeEnum } from "../types";
import { ToolTipType } from "../types";
export const OPTIMIZE_SCENARIO_COUNT = 1000;
export const SUPER_OPTIMIZE_SCENARIO_COUNT = 10000;

export const summaryText =
  "Optimize marketing spend by leveraging advanced analytics and predictive modeling techniques. Make informed decisions about budget allocation across brand, product and media channels.";
export const scenarioFormTitle = "Scenario Form";
export const scenarioFormText =
  "Enter your scenario details to explore projected results and budget recommendations.";
export const budgetText =
  "Simulate how your budget allocation and channel constraints will impact revenue targets.";
export const forecastText = "Predict the impact of your media plan with precision.";
export const optimizeText = "Jumpstart your media plan with speed and efficiency.";
export const superOptimizeText = "Achieve the ultimate optimization for your media plan.";

export const createScenarioHeader = "Welcome to Scenario Builder";
export const createScenarioDescription =
  "Our advanced AI model, Theseus, will guide you through the process of simulating your next marketing campaign. To begin, select the type of scenario you want to simulate.";
export const revenueText =
  "Simulate how your revenue goals will perform in market and explore how your various targets and channel constraints will impact your overall revenue goals.";

export const form = {
  budgetAllocationText:
    "Specify your budget for this scenario. You can allocate up to double your historical spend.",
  portfolioDistributionText:
    "Allocate budget broadly across your business, or specify constraints from brands to products. Only business units with historic spend data are displayed.",
  mediaSpendConstraintsTextDefault: [
    "Set spending rules by selecting a funnel stage, what you're advertising (brand, category, product), and the channel you are using.",
    'Selecting "All Channels" for Media Channel will allocate that input to all channels for that specific funnel stage and business rule, using your historical spending data as a guide.',
    "You don't have to fill in everything, but any unallocated budget will be distributed based on your historical spend.",
    "Note: You can only set budgets for channels with historical spend data.",
  ],
  mediaSpendConstraintsTextOptimizer: [
    "Set spending rules by selecting a funnel stage, what you're advertising (brand, category, product), and the channel you are using.",
    "You don't have to fill in everything, but any unallocated budget will be distributed based on your historical spend.",
    "Note: You can only set budgets for channels with historical spend data.",
  ],
  finalReviewText:
    "Double-check your inputs before running your scenario. Remember: any unallocated budget will be distributed based on historical spending patterns.",
  timePeriodText:
    "Scenarios are defaulted to 12 months from the last date of data available in GrowthOS.",
  disclaimer:
    "The results generated by the machine learning MMM scenario builder are based on historical data, predictive modeling, and assumptions made during the analysis. While every effort has been made to provide accurate and reliable insights, the planner's outcomes should be treated as estimates and projections rather than definitive predictions. External factors, market dynamics, and unforeseen events can influence the actual performance of marketing campaigns.",
  datePicker: "Scenarios must be within 13 months of the last date of data available in GrowthOS",
};

export const report = {
  budget: {
    roiBreakdown:
      "Based on your inputs and historic spend, this is the return on investment our model estimates you will see with this scenario.",
  },
  optimizer: {
    roiBreakdown:
      "To achieve optimal yield, our model suggests distributing your simulated marketing spend to these parts of the business.",
  },

  scenarioSummary:
    "Based on your inputs and historic spend, our model predicts your scenario will return these results.",
  revenueBreakdown:
    "Based on your inputs and historic spend, these are the parts of the business our model estimates will drive revenue in this scenario.",
};

export const getReportText = (scenarioType: ScenarioTypeEnum) => {
  switch (scenarioType) {
    case ScenarioTypeEnum.Budget:
      return report.budget;
    case ScenarioTypeEnum.Optimizer:
      return report.optimizer;
    default:
      return report.budget;
  }
};

export const tooltipText = {
  beta: "Scenario Builder is in limited release as we gather customer feedback and make further improvements. Please contact your Account Manager with any questions or suggestions.",
  timePeriod:
    "Scenario Builder starts all simulations from this date to factor in adstocks from your most recent spend. This allows us to provide more accurate ROI/Revenue confidence ranges for your simulation.",
};

export const EN_DASH = "\u2013";

export const ALL_GEOGRAPHIES_LABEL = "All Geographies";

export const MAP_SPENDBREAKDOWN_LEGEND: Record<ToolTipType, string> = {
  roi: "ROI",
  spend: "Spend",
  revenue: "Revenue",
};

export const NO_DATA = "N/A";

export const SHORT_SCENARIO_WARNING_DURATION_IN_MONTHS = 3;
export const MIN_SCENARIO_DURATION_IN_DAYS = 28;

export const NAME_ERRORS = {
  REQUIRED: "Scenario Name is required",
  MAX_LENGTH: "Name must be 128 characters or less",
};

export const START_DATE_ERRORS = {
  REQUIRED: "Start Date is required",
  TYPE_ERROR: "Start Date must be a valid date",
};

export const END_DATE_ERRORS = {
  REQUIRED: "End Date is required",
  TYPE_ERROR: "End Date must be a valid date",
};

export const TOTAL_BUDGET_ERRORS = {
  POSITIVE: "Total Budget must be a positive value",
  INTEGER: "Total Budget must be an integer",
  REQUIRED: "Total Budget is required",
  GET_MIN_MAX_MESSAGE: (minimum: string, maximum: string) =>
    `Total Budget must be between ${minimum} and ${maximum}`,
};

export const MEDIA_CONSTRAINT_ERRORS = {
  DUPLICATE:
    "A conflicting constraint already exists for this Funnel Stage/Business Unit/Channel combination.",
};

export const SHORT_SCENARIO_WARNING_MESSAGE =
  "Shorter scenarios may be less accurate than stated as they will not include the full adstocked media effect";
